import React from "react"
import { useTranslation } from "react-i18next"
import { useId } from "@reach/auto-id"
import { isReactElement } from "../../../utils"

import { handleInternalInputValidation } from "../formUtils"
import { Errors } from "../Errors"

import "./InputCheckbox.scoped.scss"
import LogoBicycode from "../../Elements/logos/LogoBicycode"
import LogoMFC from "../../Elements/logos/LogoMFC"
import { LogoMoustache } from "../../Elements/logos/LogoMoustache"
import {
    MC_OPERATOR,
    BC_OPERATOR,
    MFC_OPERATOR
} from "utils/constants"

export const InputCheckbox = ({
    // Attributes
    id,
    value,
    name,
    checked,
    label = "",
    image = "",
    checkboxLabel = "",
    disabled,
    readonly = false,
    // Styles
    style,
    className = "",
    classNameLabel = "",
    classNameInput = "",
    classNameOption = "items-center",
    // Error management
    required = false,
    customRules,
    showErrors = false,
    // Handler
    onChange,
    onValidate
}) => {
    const [errors, setErrors] = React.useState([])
    const { t } = useTranslation()

    const prefix = useId()
    const inputId = id ?? `${prefix}-${name}`
    const errorId = `${inputId}-error`

    const rules = React.useMemo(() => [
        {
            key: "checkbox-not-checked",
            check: (value) => required ? !!value : true,
            message: t("error_mandatory_field", { ns: "form" })
        },
        ...(customRules || [])
    ], [required]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback(
        (currentValue) =>
            handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
        [rules, name, onValidate]
    )

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted erros and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    }, [value, validate, onValidate, name])

    return (
        <>
            <div className={"c-form-group " + className} style={style}>
                {label && <label className={"c-label mb-1 " + classNameLabel}>{label} {required && "*"}</label>}
                <label
                    htmlFor={inputId}
                    className={"flex " + classNameOption + (value ? " is-checked" : "")}
                >
                    <input
                        className={"c-checkbox mr-5 " + classNameInput}
                        type="checkbox"
                        value={value}
                        checked={value || checked}
                        name={name}
                        id={inputId}
                        disabled={disabled || readonly}
                        required={required}
                        onChange={(e) => !readonly && onChange(e)}
                        onBlur={() => validate(value)}
                        aria-describedby={errorId}
                        aria-invalid={Boolean(errors.length)}
                    />
                    <p className="text-xs u-mg-0">
                        {checkboxLabel && (
                            isReactElement(checkboxLabel) ? React.cloneElement(checkboxLabel) : checkboxLabel
                        )}
                    </p>

                    <p className="text-xs mt-5">
                        {image &&
                            <>
                                {image === BC_OPERATOR &&
                                    <LogoBicycode width="250" height="40" />
                                }

                                {image === MFC_OPERATOR &&
                                    <LogoMFC width="200" height="35" />
                                }

                                {image === MC_OPERATOR &&
                                    <LogoMoustache width="200" height="35" />
                                }
                            </>
                        }
                    </p>
                </label>

                {/* Error messages */}
				{showErrors && <Errors id={errorId} errors={errors} />}
            </div>
        </>
    )
}
